import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { StudentLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { TradingChartLoader } from "@/components/TradingChartLoader";
import { ArrowLeft, Calendar, Clock, MapPin, User, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { registerButtonState } from "@/components/EventDetailModal";

const MONTHS = ["Januari","Februari","Mac","April","Mei","Jun","Julai","Ogos","September","Oktober","November","Disember"];
function fmt(yyyymmdd) {
  if (!yyyymmdd) return "";
  const [y, m, d] = yyyymmdd.split("-");
  return `${parseInt(d, 10)} ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

export default function StudentEventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => navigate("/student/events"));
  }, [id, navigate]);

  if (!event) return <StudentLayout title="Event"><div className="flex items-center justify-center py-16"><TradingChartLoader size="lg" text="Loading event..." /></div></StudentLayout>;

  const button = registerButtonState(event);

  return (
    <StudentLayout title={event.title}>
      <Button asChild variant="ghost" size="sm" className="mb-4" data-testid="back-to-events">
        <Link to="/student/events"><ArrowLeft className="h-4 w-4" /> Back ke Event Calendar</Link>
      </Button>
      <Card className="glass-card max-w-2xl">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <StatusBadge status={event.category} />
            <StatusBadge status={event.event_status} />
          </div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">{event.title}</h1>
          <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" />{fmt(event.event_date)}</div>
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" />{event.start_time} – {event.end_time}</div>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /><span className="capitalize">{event.location_type}</span> · {event.location || "—"}</div>
            {event.speaker_name && <div className="flex items-center gap-2"><User className="h-4 w-4 text-primary" />{event.speaker_name}</div>}
          </div>
          {event.description && (
            <p className="mt-5 text-muted-foreground leading-relaxed">{event.description}</p>
          )}
          <Button
            className="mt-6 btn-glow"
            disabled={button.disabled}
            onClick={() => event.registration_link && window.open(event.registration_link, "_blank")}
            data-testid="event-detail-register"
          >
            {!button.disabled && <ExternalLink className="h-4 w-4" />} {button.label}
          </Button>
        </CardContent>
      </Card>
    </StudentLayout>
  );
}
